import { Component, Input, OnInit } from '@angular/core';
import { InvoiceStatusComponent } from "../invoice-status/invoice-status.component";
import { CommonModule } from '@angular/common';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { EnergyByTariffPeriodComponent } from "../../charts/electricity/energy-by-tariff-period/energy-by-tariff-period.component";
import { InvoiceEnergyDoughnutChartComponent } from "../../charts/electricity/invoice-energy-doughnut-chart/invoice-energy-doughnut-chart.component";
import { TranslocoPipe } from '@jsverse/transloco';
import { ElectricityInvoice, InvoiceElectricityPODGet, InvoiceStatus, VisibilityStatus } from '../../../../core/interfaces/invoice-interfaces';
import { InvoiceHelperService } from '../../../../core/services/invoice-helper.service';
import { IsoToFormattedPeriodPipe } from '../../../../core/pipes/iso-to-formatted-period.pipe';
import { lastValueFrom } from 'rxjs';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { CommonValidatorService } from '../../../../core/services/validators/common-validator.service';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { InvoiceFileDisputeDialogComponent } from '../invoice-file-dispute-dialog/invoice-file-dispute-dialog.component';
import { InvoiceStatusTextComponent } from "../invoice-status-text/invoice-status-text.component";
import { FileUploadEnergyType } from '../../../../core/interfaces/file-upload-interfaces';

@Component({
  selector: 'app-expanded-electricity-invoice',
  standalone: true,
  imports: [InvoiceStatusComponent, CommonModule, CustomButtonComponent, EnergyByTariffPeriodComponent, InvoiceEnergyDoughnutChartComponent, TranslocoPipe, IsoToFormattedPeriodPipe, InvoiceStatusTextComponent],
  templateUrl: './expanded-electricity-invoice.component.html',
  styleUrl: './expanded-electricity-invoice.component.css'
})
export class ExpandedElectricityInvoiceComponent implements OnInit {
  @Input() invoice: ElectricityInvoice | null = null;
  @Input() statusOkColor!: string;
  @Input() disputeDialog!: InvoiceFileDisputeDialogComponent;
  @Input() reloadCallbackFunction!: () => void;

  summedPOD?: InvoiceElectricityPODGet;

  // Energy by tariff chart
  consumption: number[] = [0, 0, 0];
  kwhCosts: number[] = [0.0, 0.0, 0.0];
  labels: string[] = ['F1', 'F2', 'F3'];

  // Energy doughnut chart
  chartData: number[] = [0.0, 0.0, 0.0, 0.0, 0.0];
  kwhNumber: number = 0;

  // Toast
  @Input() toast!: ToastComponent;

  InvoiceStatus = InvoiceStatus;
  VisibilityStatus = VisibilityStatus;

  // Missing data
  forPeriodInput: string = '';
  forPeriodInputError: string | null = null;
  forPeriodDate: Date | undefined;

  invoiceNumberInput: string = '';
  invoiceNumberInputError: string | null = null;

  totalConsumptionInput: string = '';
  totalConsumptionInputError: string | null = null;

  totalCostsInput: string = '';
  totalCostsInputError: string | null = null;

  totalTaxesInput: string = '';
  totalTaxesInputError: string | null = null;

  reactiveEnergyCostsInput: string = '';
  reactiveEnergyCostsInputError: string | null = null;

  constructor(private invoiceHelper: InvoiceHelperService, private invoiceService: InvoiceService, private apiError: ApiErrorService, private commonValidator: CommonValidatorService, private dateService: DateConversionService, private dateConversion: DateConversionService) { }

  ngOnInit(): void {
    if (this.invoice == null) {
      return;
    }

    if (this.invoice) {
      this.summedPOD = this.invoiceHelper.getElectricityInvoicePODSummed(this.invoice);
    }
    this.populateEnergyByTariffChart(this.invoice!, this.summedPOD!);
    this.populateEnergyDoughnutChart(this.invoice!, this.summedPOD!);
  }

  getInvoiceStatus(invoice: ElectricityInvoice): boolean {
    if (invoice.status == InvoiceStatus.CALCULATION_FAILED) {
      return false;
    }
    return this.invoiceHelper.getElectricityInvoiceStatus(invoice);
  }

  populateEnergyByTariffChart(invoice: ElectricityInvoice, summedPOD: InvoiceElectricityPODGet) {
    if (!invoice || !summedPOD || !summedPOD.calc_data) {
      this.consumption = [0, 0, 0];
      this.kwhCosts = [0.0, 0.0, 0.0];
      this.labels = ['F1', 'F2', 'F3'];
      return;
    }

    this.consumption = [
      parseFloat((summedPOD.month_consumption_f1 ?? 0).toFixed(2)),
      parseFloat((summedPOD.month_consumption_f2 ?? 0).toFixed(2)),
      parseFloat((summedPOD.month_consumption_f3 ?? 0).toFixed(2))
    ];

    this.kwhCosts = [
      parseFloat((summedPOD.calc_data.total_unit_costs_f1 ?? 0).toFixed(2)),
      parseFloat((summedPOD.calc_data.total_unit_costs_f2 ?? 0).toFixed(2)),
      parseFloat((summedPOD.calc_data.total_unit_costs_f3 ?? 0).toFixed(2))
    ];

    this.labels = ['F1', 'F2', 'F3'];
  }

  populateEnergyDoughnutChart(invoice: ElectricityInvoice, summedPOD: InvoiceElectricityPODGet) {
    if (!invoice || !summedPOD) {
      this.chartData = [0.0, 0.0, 0.0, 0.0, 0.0];
      this.kwhNumber = 0;
      return;
    }

    const totalConsumption = invoice.total_consumption ?? 0;
    const netInvoiceAmount = summedPOD?.calc_data?.invoice_amount_net_calc ?? invoice.invoice_amount_net_actual ?? 0;

    if (netInvoiceAmount === 0 || totalConsumption === 0) {
      this.chartData = [0.0, 0.0, 0.0, 0.0, 1.0]; // Show unknown only
      this.kwhNumber = 0;
      return;
    }

    // Breakdown per unit
    const energyPerUnit = (summedPOD.calc_data?.total_material_costs ?? 0) / totalConsumption;
    const tmsPerUnit = (summedPOD.calc_data?.tms_charges_calc ?? 0) / totalConsumption;
    const dispatchPerUnit = (
      (summedPOD.dispatch_costs ?? 0) +
      (summedPOD.market_capacity_p ?? 0) +
      (summedPOD.market_capacity_op ?? 0)
    ) / totalConsumption;
    const taxesPerUnit = (summedPOD.calc_data?.total_taxes_calc ?? 0) / totalConsumption;

    // Total actual unit cost
    const actualUnitCost = netInvoiceAmount / totalConsumption;

    // Sum of known components
    const knownSum = energyPerUnit + tmsPerUnit + dispatchPerUnit + taxesPerUnit;
    const unknown = actualUnitCost > knownSum ? actualUnitCost - knownSum : 0;

    // Populate chart data in order
    this.chartData = [
      parseFloat(energyPerUnit.toFixed(2)),
      parseFloat(tmsPerUnit.toFixed(2)),
      parseFloat(dispatchPerUnit.toFixed(2)),
      parseFloat(taxesPerUnit.toFixed(2)),
      parseFloat(unknown.toFixed(2))
    ];

    // Unit cost
    this.kwhNumber = parseFloat(actualUnitCost.toFixed(2));
  }

  async downloadDisputeReport() {

    // Check if invoice is available
    if (this.invoice == null) {
      return
    }

    if (this.invoice.status == InvoiceStatus.CALCULATION_FAILED) {
      this.toast.showToast("shared.error", "invoice.not_enough_data", 'error', 5000, true);
      return;
    }

    // Download report
    try {
      var responseBlob = await lastValueFrom(this.invoiceService.getElectricityDispute(this.invoice.id ?? ''));

      const blobUrl = URL.createObjectURL(responseBlob);

      window.open(blobUrl, '_blank');
    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }
  }

  async disputeReport() {
    if (!this.invoice || this.getInvoiceStatus(this.invoice)) { return; }
    await this.disputeDialog.openModal(this.invoice.id, this.toast, this.invoice.review_requested, FileUploadEnergyType.ELECTRICITY);
  }
}
